import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import { formatPhoneNumber, personName } from '@onwardcare/core';
import { capitalizeFirstLetter } from '../../helpers/helpers';
import PersonImage from 'components/PersonImage';
import { Link } from 'react-router-dom';
// @ts-ignore
import ShowMoreText from 'react-show-more-text';
import { Ride } from '../../generated/graphql';
import { RiderImpairments } from '../riders/RiderImpairments';
import DeactivatedLabel from '../common/DeactivatedLabel';

const useStyles = makeStyles(theme => ({
  personSection: {
    marginTop: theme.spacing(2),
    height: '100%',
  },
  avatar: {
    marginRight: theme.spacing(1),
  },
  bio: {
    marginTop: theme.spacing(1),
  },
  smallBox: {
    height: '100%',
  },
  riderIcon: {
    marginRight: theme.spacing(0.75),
  },
  partner: {
    marginLeft: theme.spacing(1),
  },
}));

type Props = {
  ride: Ride;
};

const RiderCard: React.FC<Props> = ({ ride }) => {
  const classes = useStyles();

  const renderRiderDetails = () => {
    const { rider } = ride;

    if (!rider) {
      return null;
    }

    return (
      <>
        <Box display="flex">
          <Box className={classes.avatar}>
            <PersonImage person={rider} size="large" />
          </Box>
          <Box flexGrow={1}>
            <Typography variant="h6">{personName(rider)}</Typography>
            {rider.phone && (
              <Typography variant="subtitle2">
                {formatPhoneNumber(rider.phone)}
              </Typography>
            )}
          </Box>
          <RiderImpairments rider={rider} />
        </Box>
        <Box className={classes.bio}>
          {rider.notes && (
            <ShowMoreText lines={5} keepNewLines>
              {rider.notes}
            </ShowMoreText>
          )}
        </Box>
        <RenderPartnerCard />
      </>
    );
  };

  const renderRiderActions = () => {
    const { rider } = ride;

    if (rider?.deactivatedAt) {
      return <DeactivatedLabel style={{ marginTop: 8 }} />;
    }

    return (
      <Button
        color="primary"
        variant="outlined"
        component={Link}
        to={`/riders/${ride?.rider?.id}`}
        startIcon={<EditIcon />}
      >
        Edit
      </Button>
    );
  };

  const RenderPartnerCard = () => {
    const { rider } = ride;
    const carePartner = rider?.carePartner;

    if (!carePartner) {
      return null;
    }

    const relationshipType = carePartner.relationshipType.toLowerCase();
    const relationshipTypeName =
      relationshipType !== 'other'
        ? capitalizeFirstLetter(relationshipType.split('_').join(' '))
        : 'Care Partner';

    return (
      <>
        <Box display="flex">
          <Box className={classes.avatar}>
            <PersonImage person={carePartner} size="large" />
          </Box>
          <Box flexGrow={1}>
            <Typography variant="h6">
              {personName(carePartner)}
              {', '}
              {relationshipTypeName}
            </Typography>
            {ride.carePartnerProxyPhone && (
              <Typography variant="subtitle2">
                {formatPhoneNumber(ride.carePartnerProxyPhone)}
              </Typography>
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Card className={classes.personSection}>
      <CardHeader title="Rider" action={renderRiderActions()} />
      <CardContent>{renderRiderDetails()}</CardContent>
    </Card>
  );
};

export default RiderCard;
